/**
 * The price side of the custom pack card search (components.customPackCatalog.ts): the filters and the `price`
 * block of the response. Inlined by the importing files, not registered under `schemas`.
 */

export const customPackPriceParameters = [
  {
    name: 'fmv_min',
    in: 'query',
    required: false,
    schema: { type: 'number' },
    description:
      'Only cards worth at least this much (USD); a card worth exactly this much is kept. Ignored when ' +
      '`fmv_ranges[]` is sent.',
  },
  {
    name: 'fmv_max',
    in: 'query',
    required: false,
    schema: { type: 'number' },
    description:
      'Only cards worth at most this much (USD); a card worth exactly this much is kept. Ignored when ' +
      '`fmv_ranges[]` is sent.',
  },
  {
    name: 'fmv_ranges[]',
    in: 'query',
    required: false,
    schema: { type: 'array', maxItems: 10, items: { type: 'string', example: '10-25' } },
    description:
      'Price ranges to keep, in US dollars: each is `from-to`, or `from-` for no upper limit — for example ' +
      '`fmv_ranges[]=10-25&fmv_ranges[]=5000-`. Repeat the parameter per range, up to 10; unreadable entries and ' +
      'entries past the tenth are ignored. A range includes `from` and excludes `to`, and the result is the union ' +
      'of the ranges. Take the values from `price.ranges`. Takes precedence over `fmv_min` and `fmv_max`.',
  },
];

export const customPackPriceSchema = {
  type: 'object',
  title: 'CustomPackPrice',
  description:
    'The price span of the cards that can go into the pack and the price ranges to offer. Both leave the price ' +
    'filter (`fmv_ranges[]`, `fmv_min`, `fmv_max`) out and follow every other filter, so you can keep offering ' +
    'every range while one is applied. They carry no counts. Only page 1 carries `price`: later pages leave the ' +
    'key out, so keep the one you hold — a missing `price` is not an empty one.',
  required: ['min', 'max', 'ranges'],
  properties: {
    min: {
      type: 'number',
      nullable: true,
      description:
        'The lowest fair market value, in USD, among the cards that can go into the pack and match every filter ' +
        'except the price one. Null when there are none.',
    },
    max: {
      type: 'number',
      nullable: true,
      description:
        'The highest fair market value, in USD, among the cards that can go into the pack and match every filter ' +
        'except the price one. Null when there are none.',
    },
    ranges: {
      type: 'array',
      description:
        'The ranges that hold such a card, taken from a fixed ladder — 0, 5, 10, 25, 50, 100, 250, 500, 1000, ' +
        '5000, no upper limit — in ascending order. Empty when `min` and `max` are null.',
      items: {
        type: 'object',
        title: 'CustomPackPriceRange',
        required: ['from', 'to'],
        properties: {
          from: { type: 'number', description: 'Lower end in USD, included. 0 for the bottom range.' },
          to: {
            type: 'number',
            nullable: true,
            description: 'Upper end in USD, excluded. Null for the top range, which has no upper limit.',
          },
        },
      },
    },
  },
};

export const customPackPriceExample = {
  min: 16,
  max: 1000,
  ranges: [
    { from: 10, to: 25 },
    { from: 250, to: 500 },
    { from: 1000, to: 5000 },
  ],
};
